/**
 * VaultUploadModal.jsx — Upload document modal for the Career Vault.
 *
 * Fields: title, category, notes, is_public, file.
 * Shows upload progress bar during upload.
 */
import React, { useState, useRef, useCallback } from "react";
import { X, Upload, FileText, CheckCircle, AlertCircle } from "lucide-react";

const CATEGORIES = [
  { value: "certificate",       label: "Certificate" },
  { value: "experience_letter", label: "Experience Letter" },
  { value: "offer_letter",      label: "Offer Letter" },
  { value: "education",         label: "Education Document" },
  { value: "internship",        label: "Internship Certificate" },
  { value: "award",             label: "Award / Recognition" },
  { value: "project",           label: "Project PDF" },
  { value: "patent",            label: "Patent" },
  { value: "publication",       label: "Publication" },
  { value: "license",           label: "Professional License" },
  { value: "identity",          label: "Identity Document" },
  { value: "other",             label: "Other Document" },
];

const ALLOWED = ["application/pdf", "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain", "image/png", "image/jpeg", "image/jpg"];

export default function VaultUploadModal({ isOpen, onClose, onUpload, uploading, uploadProgress, theme }) {
  const isDark = theme === "dark";
  const fileRef = useRef(null);

  const [file, setFile]         = useState(null);
  const [title, setTitle]       = useState("");
  const [category, setCategory] = useState("certificate");
  const [notes, setNotes]       = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState("");

  const reset = useCallback(() => {
    setFile(null); setTitle(""); setCategory("certificate");
    setNotes(""); setIsPublic(false); setFileError(""); setDragOver(false);
  }, []);

  const handleClose = () => { reset(); onClose(); };

  const validateFile = (f) => {
    if (!f) return "Please select a file.";
    if (f.size > 10 * 1024 * 1024) return "File too large. Maximum 10 MB.";
    if (!ALLOWED.includes(f.type) && !f.name.match(/\.(pdf|docx?|txt|png|jpe?g)$/i))
      return "Unsupported file type. Use PDF, DOCX, DOC, TXT, PNG, or JPG.";
    return "";
  };

  const handleFileSelect = (f) => {
    const err = validateFile(f);
    if (err) { setFileError(err); return; }
    setFileError("");
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileSelect(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateFile(file);
    if (err) { setFileError(err); return; }
    const ok = await onUpload(file, { title: title.trim() || file.name, category, notes, is_public: isPublic });
    if (ok) { reset(); onClose(); }
  };

  if (!isOpen) return null;

  // ── Styles ────────────────────────────────────────────────────────────────
  const overlay = {
    position: "fixed", inset: 0, zIndex: 1000,
    background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 16,
  };
  const modal = {
    width: "100%", maxWidth: 480,
    background: isDark ? "#111113" : "#FFFFFF",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.09)"}`,
    borderRadius: 20, padding: "28px 28px 24px",
    boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
    display: "flex", flexDirection: "column", gap: 20,
  };
  const label = { fontSize: 11, fontWeight: 600, color: isDark ? "#A1A1AA" : "#6B7280",
    letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, display: "block" };
  const input = {
    width: "100%", padding: "9px 12px", borderRadius: 10, fontSize: 13,
    background: isDark ? "#18181B" : "#F9F9F9",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.1)"}`,
    color: isDark ? "#F4F4F5" : "#111827",
    outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && handleClose()}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: isDark ? "#F4F4F5" : "#111827" }}>
              Upload Document
            </h2>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: isDark ? "#71717A" : "#9CA3AF" }}>
              PDF, DOCX, DOC, TXT, PNG, JPG — max 10 MB
            </p>
          </div>
          <button onClick={handleClose} style={{ background: "none", border: "none", cursor: "pointer",
            color: isDark ? "#71717A" : "#9CA3AF", padding: 4, borderRadius: 6 }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Drop zone */}
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragOver ? "#7C3AED" : (file ? "#10B981" : (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"))}`,
              borderRadius: 12, padding: "20px 16px",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
              cursor: "pointer", transition: "all 0.2s ease",
              background: dragOver ? (isDark ? "#7C3AED18" : "#7C3AED08") : "transparent",
            }}
          >
            <input ref={fileRef} type="file" hidden
              accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg"
              onChange={e => { if (e.target.files[0]) handleFileSelect(e.target.files[0]); }} />
            {file ? (
              <>
                <CheckCircle size={22} color="#10B981" />
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#10B981" }}>{file.name}</p>
                <p style={{ margin: 0, fontSize: 11, color: isDark ? "#71717A" : "#9CA3AF" }}>
                  {(file.size / 1024).toFixed(1)} KB — click to change
                </p>
              </>
            ) : (
              <>
                <Upload size={22} color={isDark ? "#52525B" : "#9CA3AF"} />
                <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: isDark ? "#A1A1AA" : "#6B7280" }}>
                  Drop file here or <span style={{ color: "#7C3AED", fontWeight: 600 }}>browse</span>
                </p>
              </>
            )}
          </div>
          {fileError && (
            <div style={{ display: "flex", gap: 6, color: "#EF4444", fontSize: 12 }}>
              <AlertCircle size={14} /> {fileError}
            </div>
          )}

          {/* Title */}
          <div>
            <span style={label}>Document Title</span>
            <input style={input} value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. AWS Solutions Architect Certificate" />
          </div>

          {/* Category */}
          <div>
            <span style={label}>Category</span>
            <select style={{ ...input, cursor: "pointer" }} value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <span style={label}>Notes <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span></span>
            <textarea
              style={{ ...input, resize: "none", height: 60, fontFamily: "inherit" }}
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Issued by Amazon, valid indefinitely"
            />
          </div>

          {/* Public toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 14px", borderRadius: 10,
            background: isDark ? "#18181B" : "#F9F9F9",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"}` }}>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: isDark ? "#F4F4F5" : "#111827" }}>
                Make Public
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 11, color: isDark ? "#71717A" : "#9CA3AF" }}>
                Recruiters can view this document via your share link
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsPublic(p => !p)}
              style={{
                width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
                background: isPublic ? "#7C3AED" : (isDark ? "#3F3F46" : "#D1D5DB"),
                transition: "background 0.2s ease", position: "relative", flexShrink: 0,
              }}
            >
              <span style={{
                position: "absolute", top: 3, left: isPublic ? 22 : 3,
                width: 18, height: 18, borderRadius: "50%", background: "#fff",
                transition: "left 0.2s ease", boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              }} />
            </button>
          </div>

          {/* Upload progress */}
          {uploading && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: isDark ? "#A1A1AA" : "#6B7280" }}>Uploading…</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#7C3AED" }}>{uploadProgress}%</span>
              </div>
              <div style={{ height: 4, borderRadius: 4, background: isDark ? "#27272A" : "#E5E7EB" }}>
                <div style={{
                  height: "100%", borderRadius: 4,
                  background: "linear-gradient(90deg, #7C3AED, #A78BFA)",
                  width: `${uploadProgress}%`, transition: "width 0.3s ease",
                }} />
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" onClick={handleClose}
              disabled={uploading}
              style={{
                padding: "9px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600,
                background: "none",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
                color: isDark ? "#A1A1AA" : "#6B7280", cursor: "pointer",
              }}>
              Cancel
            </button>
            <button type="submit"
              disabled={uploading || !file}
              style={{
                padding: "9px 24px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                background: uploading || !file ? (isDark ? "#3F3F46" : "#E5E7EB") : "linear-gradient(135deg, #7C3AED, #6D28D9)",
                color: uploading || !file ? (isDark ? "#71717A" : "#9CA3AF") : "#FFFFFF",
                border: "none", cursor: uploading || !file ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
              }}>
              {uploading ? "Uploading…" : "Upload Document"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
